import { getMinerModels } from '@/services/core.service'
import type { IOptionsComposableVueQueryOptions } from '@/types/shared/vue-query'
import { useQuery } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref, watchEffect } from 'vue'
import { uniq } from 'lodash'

/**
 * Composable for getMinerModels response, mapped to ElSelect options.
 *
 * @param refetchOnWindowFocus default: true; useQuery refetch on window focus.
 * @param refetchOnMount default: true; useQuery refetch on mount.
 * @returns getMinerModels useQuery state.
 */
export const useMinerModelOptions = (
  { refetchOnWindowFocus, refetchOnMount, enabled }: IOptionsComposableVueQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: ref(true),
  }
) => {
  const minerModelNameOptions = ref<OptionType[]>([])
  const minerModelFamilyOptions = ref<OptionType[]>([])

  const { isFetching, isRefetching, isError, data, error, refetch } = useQuery({
    queryKey: ['miner-model-options'],
    queryFn: getMinerModels,
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  })

  watchEffect(() => {
    if (!data.value) {
      minerModelNameOptions.value = []
      minerModelFamilyOptions.value = []
    } else {
      minerModelNameOptions.value = data.value.map((mm) => ({
        label: mm.name,
        value: mm.name,
      }))
      minerModelFamilyOptions.value = uniq(
        data.value.map((mm) => ({
          label: mm.family,
          value: mm.family,
        }))
      )
    }
  })

  return {
    isFetching,
    isRefetching,
    isError,
    data,
    error,
    refetch,
    enabled,
    minerModelNameOptions,
    minerModelFamilyOptions,
  }
}
